<template>
  <div ref="schedule" style="height: 100%"></div>
</template>

<script>
import { useVisTimeline } from '../composables/useVisTimeline'
import {
  bookingToVisItem,
  UNASSIGNED_GROUP_ID,
  unassignedGroup,
  visGroups,
  visItems,
  visItemTooltip,
  visOptions,
} from './schedule-utils'
import '../assets/scss/schedule-vis-timeline.scss'

export default {
  name: 'DriversScheduleTimeline',

  props: {
    schedule: {
      type: Object,
      default: null,
    },
    range: {
      type: Object,
      required: true,
    },
    showUnassigned: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      chart: null,
      groups: null,
      items: null,
      wasDragged: false,
    }
  },

  watch: {
    schedule(newVal) {
      if (!newVal) return
      this.drawChart()
    },

    showUnassigned(value) {
      if (!this.groups) return
      this.groups.update({ id: UNASSIGNED_GROUP_ID, visible: value })
    },
  },

  beforeDestroy() {
    this.destroyChart()
  },

  methods: {
    createChart() {
      if (this.chart) {
        return
      }
      this.chart = useVisTimeline(
        this.$refs.schedule,
        [],
        [],
        visOptions({
          locale: this.$i18n.locale,
          onAdd: this.onItemAdd,
          onMoving: this.onItemMoving,
          onMove: this.onItemMove,
          tooltip: {
            delay: 300,
            followMouse: true,
            overflowMethod: 'flip',
            template: this.tooltipTemplate,
          },
          xss: {
            disabled: true,
          },
        })
      )
      this.chart.on('click', this.handleClick)
      this.chart.on('rangechange', () => (this.wasDragged = true))
    },

    drawChart() {
      if (!this.schedule) {
        return
      }

      this.createChart()
      this.items = visItems(this.schedule.data)
      this.groups = visGroups(this.schedule.drivers)
      this.groups.add(unassignedGroup(this.$t.bind(this), this.showUnassigned))

      let max = this.$moment(this.range.max).endOf('day')
      this.items.forEach((item) => {
        if (item.end.getTime() > max.valueOf()) {
          max = this.$moment(item.end).endOf('hour')
        }
      })

      const options = {
        min: this.$moment(this.range.min).startOf('day'),
        max: max.endOf('hour'),
      }

      this.chart.setOptions(options)
      setTimeout(() => {
        this.chart.moveTo(options.min, () => (this.wasDragged = false))
        this.chart.setData({ items: this.items, groups: this.groups })
        // moveTo triggers rangechange,
        // but callback is not called when already in range
        this.wasDragged = false
      }, 100)
    },

    destroyChart() {
      if (this.chart) {
        this.chart.destroy()
        this.chart = null
      }
    },

    handleClick(properties) {
      if (this.wasDragged) {
        this.wasDragged = false
        return
      }

      if (properties.what === 'item') {
        this.$emit('item-click', properties.item)
        return
      }

      if (properties.what === 'background') {
        this.onItemAdd({ start: properties.time, group: properties.group })
        return
      }

      if (
        properties.what === 'group-label' &&
        properties.group !== UNASSIGNED_GROUP_ID
      ) {
        this.$emit('group-click', properties.group)
      }
    },

    onItemAdd(item) {
      this.$emit('item-add', {
        start: item.start,
        driver_id: item.group === UNASSIGNED_GROUP_ID ? null : item.group,
      })
    },

    onItemMoving(item, callback) {
      this.wasDragged = true
      callback(item)
    },

    onItemMove(item, callback) {
      const { group } = item

      const hasOverlap = this.items
        .get()
        .some(
          (i) =>
            i.id !== item.id &&
            i.group === item.group &&
            i.start < item.end &&
            item.start < i.end
        )

      this.$emit('item-move', {
        item,
        newDriverId: item.group === UNASSIGNED_GROUP_ID ? null : item.group,
        hasOverlap,
      })

      callback({
        id: item.id,
        driverId: group === UNASSIGNED_GROUP_ID ? null : group,
        group: item.group,
      })
    },

    revertMove(item) {
      item.group = item.driverId ? item.driverId : UNASSIGNED_GROUP_ID
      this.items.updateOnly(item)
    },

    tooltipTemplate(item) {
      return visItemTooltip(item, this.$t.bind(this))
    },

    updateGroup(data) {
      if (this.groups) {
        this.groups.update({ id: data.id, content: data.name })
      }
    },

    addOrUpdateBooking(booking) {
      if (!this.items) {
        return
      }

      const item = bookingToVisItem(booking)
      const exists = this.items.get(item.id)
      if (exists) {
        this.items.updateOnly(item)
        return
      }

      this.addItem(item)
    },

    addItem(item) {
      if (this.items) {
        this.items.add(item)
      }
    },

    deleteItem(item) {
      if (this.items) {
        this.items.remove(item.id)
      }
    },
  },
}
</script>
